/** @format */
import { Transition } from '@mpb-com/feather/components/atoms/transition';
import { useTranslation } from 'next-i18next';
import React, {
    Fragment,
    FunctionComponent,
    ReactNode,
    useContext,
} from 'react';
import styled from 'styled-components';

import CheckmarkIcon from '@toucan/client/global/icons/checkmark.svg';
import PlusIcon from '@toucan/client/global/icons/plus.svg';
import TradeArrowIcon from '@toucan/client/global/icons/trade-arrow.svg';

import { Link } from '@toucan/client/shared/components/link';
import { SellFormContext } from '@toucan/client/shared/context/sell-form';

import { useTradeForThisHandler } from '@toucan/client/views/product/components/product-add-to-quote-link/hooks/use-trade-for-this-handler';
import { ProductAddToQuoteLinkSkeleton } from '@toucan/client/views/product/components/product-add-to-quote-link/skeleton';

import { DEFAULT_TEST_ID } from './constants';
import { styles, wrapperStyles } from './product-add-to-quote-link.styles';
import {
    ProductAddToQuoteLinkProps,
    StyledProductAddToQuoteLinkProps,
} from './types';

const StyledProductAddToQuoteLink = styled(
    Link
)<StyledProductAddToQuoteLinkProps>`
    ${styles}
`;

// eslint-disable-next-line @mpb-com/mpb/single-styled-component -- Legacy code: do not use as an example.
export const StyledProductAddToQuoteLinkWrapper = styled('div')`
    ${wrapperStyles}
`;

export const ProductAddToQuoteLink: FunctionComponent<
    ProductAddToQuoteLinkProps
> = ({
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    productData,
    refreshProductData,
    ...otherProps
}) => {
    const { t } = useTranslation();
    const { handleTradeForThis, hasOpenQuote, isInOpenQuote, isAddingToQuote } =
        useTradeForThisHandler({ productData, refreshProductData });
    const { sellForm } = useContext(SellFormContext);

    const linkVariants: {
        prefix: ReactNode;
        text: string;
        isVisible: boolean;
    }[] = [
        {
            prefix: (
                <CheckmarkIcon
                    data-testid={`${dataTestId}__checkmark-icon`}
                    className="add-to-quote-icon checkmark"
                />
            ),
            text: t('Added to quote'),
            isVisible: isInOpenQuote,
        },
        {
            prefix: (
                <PlusIcon
                    data-testid={`${dataTestId}__plus-icon`}
                    className="add-to-quote-icon plus"
                />
            ),
            text: t('Add to open quote'),
            isVisible: !isInOpenQuote && hasOpenQuote,
        },
        {
            prefix: (
                <Fragment>
                    <TradeArrowIcon
                        data-testid={`${dataTestId}__trade-arrow-icon`}
                        className="add-to-quote-icon trade-arrow top"
                    />
                    <TradeArrowIcon
                        data-testid={`${dataTestId}__trade-arrow-icon`}
                        className="add-to-quote-icon trade-arrow top-hidden"
                    />
                    <TradeArrowIcon
                        data-testid={`${dataTestId}__trade-arrow-icon`}
                        className="add-to-quote-icon trade-arrow bottom"
                    />
                    <TradeArrowIcon
                        data-testid={`${dataTestId}__trade-arrow-icon`}
                        className="add-to-quote-icon trade-arrow bottom-hidden"
                    />
                </Fragment>
            ),
            text: t('Lower the cost when you trade'),
            isVisible: !isInOpenQuote && !hasOpenQuote,
        },
    ];

    if (!sellForm.hasInitialised) {
        return <ProductAddToQuoteLinkSkeleton />;
    }

    return (
        <StyledProductAddToQuoteLinkWrapper>
            {linkVariants.map(({ isVisible, prefix, text }) => (
                <Transition key={text} durationMs={600} isVisible={isVisible}>
                    {({ props, isAnimating }) => (
                        <StyledProductAddToQuoteLink
                            {...otherProps}
                            {...props}
                            $isAnimating={isAnimating}
                            $isAddingToQuote={isAddingToQuote}
                            // eslint-disable-next-line @mpb-com/mpb/transient-props-last -- Legacy code: do not use as an example.
                            data-testid={dataTestId}
                            disabled={isAddingToQuote}
                            // eslint-disable-next-line @typescript-eslint/no-misused-promises -- Legacy code: do not use as an example.
                            onClick={handleTradeForThis}
                            prefix={prefix}
                        >
                            {text}
                        </StyledProductAddToQuoteLink>
                    )}
                </Transition>
            ))}
        </StyledProductAddToQuoteLinkWrapper>
    );
};
